import { UpperCasePipe } from '@angular/common'
import { Component, DestroyRef, Injector, computed, inject } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { MenuItem } from 'primeng/api'
import { MenuModule } from 'primeng/menu'
import { TooltipModule } from 'primeng/tooltip'

import { map } from 'rxjs'

import { FlxButtonComponent, FlxModalService } from '@flowx/angular-ui-toolkit'
import {
  HeaderAction,
  HeaderObject,
  type UserMenuItem,
} from '@flowx/designer/modules/analytics/analytics-header.entity'
import { selectAnalyticsArea } from '@flowx/designer/modules/analytics/analytics-header.selectors'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { AuthConfigService } from '@flowx/designer/modules/auth/auth.service'
import { TOOLTIP_DEFAULT_DELAY } from '@flowx/designer/modules/core/dictionary/constants'
import {
  AccessManagementStore,
  OrgAdminAccessManagementStore,
} from '@flowx/designer/modules/workspaces/shared/store/access-management.stores'
import { UserModalComponent } from '@flowx/designer/modules/workspaces/shared/users/user-modal/user-modal.component'
import {
  ACCESS_MANAGEMENT_GROUPS_API,
  ACCESS_MANAGEMENT_ROLES_API,
  provideOrganizationAdminUsersApis,
} from '@flowx/designer/modules/workspaces/shared/workspaces'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import { OrganisationAdminRolesApiService } from '@flowx/designer/shared/api/organisation-admin-roles.api'
import { OrganizationAdminGroupsApiService } from '@flowx/designer/shared/api/organization-admin-groups.api'
import { UsersType } from '@flowx/designer/shared/entities/utils'

/** The avatar at the far right of the global header, and the account menu it opens. */
@Component({
  selector: 'flx-account-menu',
  imports: [UpperCasePipe, MenuModule, TooltipModule, FlxButtonComponent],
  templateUrl: './account-menu.component.html',
  styleUrl: './account-menu.component.scss',
  // What the user's own settings modal resolves its APIs from: it opens with this injector.
  providers: [
    OrgAdminAccessManagementStore,
    { provide: AccessManagementStore, useExisting: OrgAdminAccessManagementStore },
    provideOrganizationAdminUsersApis(),
    { provide: ACCESS_MANAGEMENT_GROUPS_API, useClass: OrganizationAdminGroupsApiService },
    { provide: ACCESS_MANAGEMENT_ROLES_API, useClass: OrganisationAdminRolesApiService },
  ],
})
export class FlxAccountMenuComponent {
  #authConfigService = inject(AuthConfigService)
  #modalService = inject(FlxModalService)
  #router = inject(Router)
  #injector = inject(Injector)
  #destroyRef = inject(DestroyRef)
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  #analyticsService = inject(AnalyticsService)
  #analyticsArea = inject(Store).selectSignal(selectAnalyticsArea)

  /** The refresh the menu started on opening; Organization settings waits for it before leaving. */
  #organisationPermissionsRefresh: Promise<void> = Promise.resolve()

  protected TOOLTIP_DEFAULT_DELAY = TOOLTIP_DEFAULT_DELAY

  // The name shown follows a profile edit: the token carries it, and a refresh brings it in.
  currentUser = toSignal(
    this.#authConfigService.tokenRefreshed$.pipe(map(() => this.#authConfigService.userInfo)),
    { initialValue: this.#authConfigService.userInfo }
  )

  userIsOrganisationAdmin = this.#workspacesAndPermissionsStore.userIsOrganisationAdmin

  menuItems = computed((): MenuItem[] => [
    {
      label: 'Profile settings',
      icon: 'ph ph-user',
      automationId: 'user-menu-settings-option',
      command: (): void => {
        this.#trackItemSelected('profile_settings')
        this.openProfileSettings()
      },
    },
    {
      label: 'Organization settings',
      icon: 'ph ph-gear',
      automationId: 'user-menu-organization-settings-option',
      visible: this.userIsOrganisationAdmin(),
      // The menu shows before its refresh has answered: leave only if the user is still an Org Admin.
      command: async (): Promise<void> => {
        this.#trackItemSelected('organization_settings')
        await this.#organisationPermissionsRefresh

        if (this.userIsOrganisationAdmin()) {
          await this.#router.navigateByUrl('/guard/organisation-admin')
        }
      },
    },
    { separator: true },
    {
      label: 'Log out',
      icon: 'ph ph-sign-out',
      automationId: 'user-menu-logout-option',
      command: (): void => {
        this.#trackItemSelected('logout')
        this.#authConfigService.logout()
      },
    },
  ])

  /** A user demoted while logged in sees Organization settings drop out of the open menu. */
  refreshOrganisationPermissions(): void {
    this.#organisationPermissionsRefresh =
      this.#workspacesAndPermissionsStore.refreshOrganisationPermissions()
  }

  /** Opens the current user's own details, editable, password change included. */
  openProfileSettings(): void {
    this.#modalService
      .show(
        UserModalComponent,
        { closeOnBackdropClick: false, backdropClass: 'overlay-backdrop' },
        {
          userId: this.currentUser().id,
          usersType: UsersType.SELF,
          canEdit: true,
          isSelfEdit: true,
          canChangePassword: true,
        },
        this.#injector
      )
      .data$.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe()
  }

  #trackItemSelected(item: UserMenuItem): void {
    this.#analyticsService.trackHeader({
      event: { object: HeaderObject.USER_MENU, action: HeaderAction.SELECTED },
      properties: { item, area: this.#analyticsArea() },
    })
  }
}
